import { useMemo } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type GradientConfig, gradientToCss } from "@/lib/tier";

type Props = {
  customPresets?: { id: string; name: string; config: GradientConfig }[];
  canManageCustomPresets?: boolean;
  savePresetName?: string;
  onSavePresetNameChange?: (name: string) => void;
  onSavePreset?: () => void;
  onApplyCustomPreset?: (presetId: string) => void;
  onDeleteCustomPreset?: (presetId: string) => void;
  isSavingPreset?: boolean;
  deletingPresetId?: string | null;
};

export const GradientPresetManager = ({
  customPresets = [],
  canManageCustomPresets = false,
  savePresetName = "",
  onSavePresetNameChange,
  onSavePreset,
  onApplyCustomPreset,
  onDeleteCustomPreset,
  isSavingPreset = false,
  deletingPresetId = null,
}: Props) => {
  const trimmedPresetName = savePresetName.trim();
  const saveButtonLabel = useMemo(() => {
    if (!trimmedPresetName) return "Save preset";
    return customPresets.some((preset) => preset.name.toLowerCase() === trimmedPresetName.toLowerCase()) ? "Overwrite preset" : "Save preset";
  }, [customPresets, trimmedPresetName]);

  return (
    <div className="min-w-0 space-y-3 rounded-md border border-border p-3">
      <div>
        <Label className="text-xs">My presets</Label>
        <p className="mt-1 text-[11px] text-muted-foreground">Save the exact gradient you have now so you can reuse it later.</p>
      </div>

      {canManageCustomPresets ? (
        <div className="space-y-3">
          {customPresets.length ? (
            <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {customPresets.map((preset) => (
                <div key={preset.id} className="group flex min-w-0 flex-col gap-1.5 rounded-md border border-border p-2">
                  <button
                    type="button"
                    onClick={() => onApplyCustomPreset?.(preset.id)}
                    className="flex min-w-0 flex-col items-stretch gap-1 text-left"
                    aria-label={`Apply ${preset.name} preset`}
                  >
                    <div className="h-10 w-full rounded" style={{ background: gradientToCss(preset.config) }} />
                    <span className="truncate text-[11px] text-muted-foreground group-hover:text-foreground">{preset.name}</span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 justify-start px-1 text-[11px] text-muted-foreground hover:text-foreground"
                    onClick={() => onDeleteCustomPreset?.(preset.id)}
                    disabled={deletingPresetId === preset.id}
                    aria-label={`Delete ${preset.name} preset`}
                  >
                    {deletingPresetId === preset.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">No saved presets yet.</p>
          )}

          <div className="flex min-w-0 flex-col gap-2 border-t border-border pt-3 sm:flex-row">
            <Input
              className="min-w-0"
              value={savePresetName}
              onChange={(event) => onSavePresetNameChange?.(event.target.value)}
              placeholder="Name this preset"
              maxLength={60}
              aria-label="Preset name"
            />
            <Button
              type="button"
              size="sm"
              onClick={onSavePreset}
              disabled={!trimmedPresetName || isSavingPreset}
              className="gap-1 sm:self-start"
            >
              {isSavingPreset ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saveButtonLabel}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">Custom saved presets are available on Premium.</p>
      )}
    </div>
  );
};